import { FormattedMessage } from 'react-intl';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CircleOutlined from '@mui/icons-material/CircleOutlined';
import ContentCut from '@mui/icons-material/ContentCut';
import CropSquareOutlined from '@mui/icons-material/CropSquareOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import DeleteSweep from '@mui/icons-material/DeleteSweep';
import JoinFull from '@mui/icons-material/JoinFull';
import JoinInner from '@mui/icons-material/JoinInner';
import MergeType from '@mui/icons-material/MergeType';
import PolylineOutlined from '@mui/icons-material/PolylineOutlined';
import PanToolAltOutlined from '@mui/icons-material/PanToolAltOutlined';
import Redo from '@mui/icons-material/Redo';
import Undo from '@mui/icons-material/Undo';
import Fullscreen from '@mui/icons-material/Fullscreen';
import FullscreenExit from '@mui/icons-material/FullscreenExit';
import ZipBulkImportIcon from '@mui/icons-material/UploadFile';
import difference from '@turf/difference';
import { featureCollection } from '@turf/helpers';
import intersect from '@turf/intersect';
import union from '@turf/union';
import { nanoid } from 'nanoid';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';

import {
  attachPolygonGeometryListeners,
  createEditablePolygonFromCircle,
  createEditablePolygonFromRectangle,
  enablePolygonVertices,
  featureToGooglePolygons,
  latLngBoundsFromFeature,
  overlayToFeature,
  polygonFeatureFromLatLngBounds,
} from './overlayGeo';
import ZoneControls, { ZONE_VISIBILITY_ALL } from './ZoneControls';

/** Stable id for the auto-created default service area (radius + center mode). */
export const DEFAULT_ZONE_ID = 'sxcl-default-zone';

const DEFAULT_ZONE_DEF = Object.freeze({
  id: DEFAULT_ZONE_ID,
  name: 'Default Service Area',
  color: '#1976d2',
  zoneType: 'regular',
});

const defaultCenter = { lat: 39.8283, lng: -98.5795 };

const defaultMapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  /** Native map fullscreen hides our MUI toolbars; we use shell fullscreen instead. */
  fullscreenControl: false,
};

/** @param {HTMLElement} el */
function requestFullscreenElement(el) {
  const req =
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.mozRequestFullScreen ||
    el.msRequestFullscreen;
  return req ? req.call(el) : undefined;
}

function exitFullscreenDocument() {
  const exit =
    document.exitFullscreen ||
    document.webkitExitFullscreen ||
    document.mozCancelFullScreen ||
    document.msExitFullscreen;
  return exit ? exit.call(document) : undefined;
}

function getFullscreenElement() {
  return (
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement ||
    null
  );
}

const HISTORY_LIMIT = 50;

/** US / international statute mile (exact definition used by Google Maps tooling). */
export const METERS_PER_STATUTE_MILE = 1609.344;

/** @param {unknown[]} z @param {unknown[]} a */
function serializeZoneModel(z, a) {
  return JSON.stringify({ zones: z, areas: a });
}

/**
 * Payload for Save changes: one row per zone, each GeoJSON feature is one drawn shape.
 * @param {Array<{ id: string; name: string; color: string; zoneType: string }>} zones
 * @param {Array<{ zoneId: string; feature?: import('@turf/helpers').Feature }>} areas
 * @returns {Array<{ zone: { name: string; colorCode: string; type: string }; geoJson: import('@turf/helpers').Feature[] }>}
 */
function buildZoneSavePayload(zones, areas) {
  return zones.map(z => ({
    zone: {
      name: z.name,
      colorCode: z.color,
      type: z.zoneType,
    },
    geoJson: areas
      .filter(a => a.zoneId === z.id)
      .map(a => a.feature)
      .filter(Boolean),
  }));
}

const SXCL_KEY = 'sxcl';

/** @param {google.maps.MVCObject} overlay */
function getOverlayMeta(overlay) {
  return overlay.get(SXCL_KEY) ?? null;
}

/** @param {google.maps.MVCObject} overlay */
function setOverlayMeta(overlay, meta) {
  overlay.set(SXCL_KEY, meta);
}

const ZIP_IMPORT_LIMIT = 250;

/** @param {string} raw */
function parseZipCodesFromCsv(raw) {
  if (!raw || typeof raw !== 'string') return [];
  return [
    ...new Set(
      raw
        .split(/[\s,;]+/u)
        .map(s => s.trim())
        .filter(Boolean),
    ),
  ];
}

/** @param {unknown} code ISO 3166-1 alpha-2; defaults to US if missing or invalid. */
function normalizeBulkImportCountryCode(code) {
  if (code == null || typeof code !== 'string') return 'US';
  const t = code.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(t) ? t : 'US';
}

/**
 * Only use Geocoder rows typed as postal_code so we never draw country/region viewports.
 * @param {google.maps.GeocoderResult[]|undefined|null} results
 * @returns {google.maps.LatLngBounds|null}
 */
function pickPostalCodeGeocodeBounds(results) {
  if (!Array.isArray(results) || results.length === 0) return null;
  const postal = results.find(
    r => r && Array.isArray(r.types) && r.types.includes('postal_code'),
  );
  if (!postal?.geometry) return null;
  const { bounds, viewport } = postal.geometry;
  return bounds || viewport || null;
}

/**
 * @param {string} postalCode
 * @param {string} [countryCode] ISO 3166-1 alpha-2 (e.g. US, CA, GB). Defaults to US.
 */
function geocodePostalCodeToBounds(postalCode, countryCode) {
  const cc = normalizeBulkImportCountryCode(countryCode);
  return new Promise(resolve => {
    const g = window.google.maps;
    if (!g?.Geocoder) {
      resolve(null);
      return;
    }
    const geocoder = new g.Geocoder();
    const q = String(postalCode).trim();
    const usZip = cc === 'US' ? q.match(/^(\d{5})(?:-\d{4})?$/) : null;
    const primaryRequest = usZip
      ? {
          address: usZip[1],
          componentRestrictions: {
            country: cc,
            postalCode: usZip[1],
          },
        }
      : {
          address: q,
          componentRestrictions: { country: cc },
        };

    geocoder.geocode(primaryRequest, (results, status) => {
      if (status === 'OK') {
        const b = pickPostalCodeGeocodeBounds(results);
        if (b) {
          resolve(b);
          return;
        }
      }
      if (usZip && primaryRequest.componentRestrictions?.postalCode) {
        geocoder.geocode(
          { address: q, componentRestrictions: { country: cc } },
          (results2, status2) => {
            if (status2 === 'OK') {
              resolve(pickPostalCodeGeocodeBounds(results2));
            } else {
              resolve(null);
            }
          },
        );
        return;
      }
      resolve(null);
    });
  });
}

function delay(ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

/**
 * Geocode postal codes one-by-one with a small delay to reduce OVER_QUERY_LIMIT risk.
 * @param {string[]} zips
 * @param {string} [countryCode] ISO 3166-1 alpha-2 for Geocoder `componentRestrictions.country`.
 * @returns {Promise<{ entries: Array<{ zip: string; bounds: google.maps.LatLngBounds }>; geocodeFailed: string[] }>}
 */
async function geocodeZipListSequential(zips, countryCode) {
  const cc = normalizeBulkImportCountryCode(countryCode);
  const geocodeFailed = [];
  const entries = [];
  /* Sequential calls + throttle for Geocoder quota. */
  /* eslint-disable no-await-in-loop */
  for (let i = 0; i < zips.length; i += 1) {
    const zip = zips[i];
    if (i > 0) {
      await delay(150);
    }
    const bounds = await geocodePostalCodeToBounds(zip, cc);
    if (bounds) {
      entries.push({ zip, bounds });
    } else {
      geocodeFailed.push(zip);
    }
  }
  /* eslint-enable no-await-in-loop */
  return { entries, geocodeFailed };
}

const shapeStyleNeutral = selected => ({
  strokeColor: selected ? '#0d47a1' : '#5f6368',
  strokeWeight: selected ? 3 : 2,
  fillColor: '#1976d2',
  fillOpacity: selected ? 0.35 : 0.22,
});

function shapeStyleBaseFromZoneColor(color, selected) {
  return {
    strokeColor: selected ? '#0d47a1' : color,
    strokeWeight: selected ? 3 : 2,
    fillColor: color,
    fillOpacity: selected ? 0.45 : 0.28,
  };
}

function applyShapeStyleForOverlay(overlay, selected, zones) {
  const meta = getOverlayMeta(overlay);
  const zid = meta?.zoneId;
  const zone = zones.find(z => z.id === zid);
  const color = zone?.color ?? '#1976d2';
  overlay.setOptions(shapeStyleBaseFromZoneColor(color, selected));
}

function resolveMergedZoneId(selectedOverlays, drawTargetZoneId) {
  const ids = [
    ...new Set(
      selectedOverlays.map(o => getOverlayMeta(o)?.zoneId).filter(Boolean),
    ),
  ];
  if (ids.length === 1) return ids[0];
  if (ids.length > 1 && drawTargetZoneId) return drawTargetZoneId;
  return null;
}

/**
 * Symmetric difference of two polygonal features: (A \ B) ∪ (B \ A) — overlap removed.
 * @param {import('@turf/helpers').Feature} a
 * @param {import('@turf/helpers').Feature} b
 * @returns {import('@turf/helpers').Feature|null}
 */
function symmetricDifferencePair(a, b) {
  if (!a?.geometry || !b?.geometry) return null;
  const aMinusB = difference(featureCollection([a, b]));
  const bMinusA = difference(featureCollection([b, a]));
  const parts = [aMinusB, bMinusA].filter(f => f?.geometry);
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0];
  return union(featureCollection(parts));
}

/**
 * Fold symmetric difference over ordered polygon features (multi-select XOR).
 * @param {import('@turf/helpers').Feature[]} features
 * @returns {import('@turf/helpers').Feature|null}
 */
function symmetricDifferenceMany(features) {
  if (!Array.isArray(features) || features.length < 2) return null;
  let acc = features[0];
  for (let i = 1; i < features.length; i += 1) {
    acc = symmetricDifferencePair(acc, features[i]);
    if (!acc?.geometry) return null;
  }
  return acc;
}

/**
 * GeoJSON Feature from a place object when your pipeline attaches it (e.g. `geojson`,
 * or `geometry` as Polygon/MultiPolygon). Ignores classic Google `geometry.viewport`.
 * @param {unknown} place
 * @returns {import('@turf/helpers').Feature|null}
 */
function extractExplicitGeoJsonFeature(place) {
  if (!place || typeof place !== 'object') return null;

  const wrapGeom = geom => {
    if (!geom || typeof geom !== 'object') return null;
    if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
      return {
        type: 'Feature',
        properties: {},
        geometry: geom,
      };
    }
    return null;
  };

  const tryValue = v => {
    if (v == null) return null;
    if (typeof v === 'string') {
      try {
        return tryValue(JSON.parse(v));
      } catch {
        return null;
      }
    }
    if (typeof v !== 'object') return null;
    if (v.type === 'Feature' && v.geometry) {
      return wrapGeom(v.geometry) ? v : null;
    }
    return wrapGeom(v);
  };

  const fromKnownKeys =
    tryValue(place.geojson) ||
    tryValue(place.geoJson) ||
    tryValue(place.boundary);
  if (fromKnownKeys) return fromKnownKeys;

  const gh = place.geometry;
  if (
    gh &&
    typeof gh === 'object' &&
    !Object.prototype.hasOwnProperty.call(gh, 'viewport') &&
    !Object.prototype.hasOwnProperty.call(gh, 'location')
  ) {
    const wrapped = wrapGeom(gh);
    if (wrapped) return wrapped;
  }

  if (typeof place.toJSON === 'function') {
    try {
      const json = place.toJSON();
      if (json && json !== place) {
        const nested = extractExplicitGeoJsonFeature(json);
        if (nested) return nested;
      }
    } catch {
      /* ignore */
    }
  }

  return null;
}

/**
 * @param {{ viewport?: google.maps.LatLngBounds; location?: google.maps.LatLng }} fields
 */
function boundsFromGoogleViewportOrLocation(fields) {
  const g = window.google.maps;
  if (!g) return null;
  const { viewport, location } = fields;
  if (viewport) {
    if (viewport instanceof g.LatLngBounds) return viewport;
    if (
      typeof viewport.getNorthEast === 'function' &&
      typeof viewport.getSouthWest === 'function'
    ) {
      return viewport;
    }
  }
  if (location) {
    const spherical = g.geometry?.spherical;
    if (!spherical) return null;
    const c =
      typeof location.lat === 'function'
        ? location
        : new g.LatLng(location.lat, location.lng);
    const nw = spherical.computeOffset(c, 520, 315);
    const se = spherical.computeOffset(c, 520, 135);
    return new g.LatLngBounds(nw, se);
  }
  return null;
}

/**
 * GeoJSON Feature for the map: explicit polygon on the place, else rectangle from viewport/location.
 * @param {unknown} place
 * @param {{ viewport?: *; location?: * }|null|undefined} legacyGeometry
 * @param {google.maps.LatLngBounds|null|undefined} searchBoundsOverride Prefer geocoded bounds over Places viewport when set.
 */
function geoJsonFeatureFromGooglePlaceSearch(
  place,
  legacyGeometry,
  searchBoundsOverride,
) {
  const explicit = extractExplicitGeoJsonFeature(place);
  if (explicit) return explicit;

  const viewport =
    searchBoundsOverride ??
    place?.viewport ??
    legacyGeometry?.viewport ??
    place?.geometry?.viewport;
  const location =
    place?.location ?? legacyGeometry?.location ?? place?.geometry?.location;

  const bounds = boundsFromGoogleViewportOrLocation({ viewport, location });
  if (!bounds) return null;
  return polygonFeatureFromLatLngBounds(bounds);
}

/** @param {string|undefined|null} id */
function normalizePlaceResourceId(id) {
  if (!id || typeof id !== 'string') return '';
  return id.startsWith('places/') ? id.slice('places/'.length) : id;
}

/** @param {string[]|undefined|null} types */
function inferFeatureTypeFromPlaceTypes(types) {
  const FT = window.google?.maps?.FeatureType;
  if (!FT || !Array.isArray(types)) return null;
  const set = new Set(types);
  if (set.has('postal_code')) return FT.POSTAL_CODE;
  if (
    typeof FT.NEIGHBORHOOD !== 'undefined' &&
    (set.has('neighborhood') || set.has('neighbourhood'))
  ) {
    return FT.NEIGHBORHOOD;
  }
  if (
    set.has('locality') ||
    set.has('sublocality') ||
    set.has('sublocality_level_1')
  ) {
    return FT.LOCALITY;
  }
  if (set.has('administrative_area_level_2')) {
    return FT.ADMINISTRATIVE_AREA_LEVEL_2;
  }
  if (set.has('administrative_area_level_1')) {
    return FT.ADMINISTRATIVE_AREA_LEVEL_1;
  }
  if (set.has('country')) return FT.COUNTRY;
  return null;
}

/** @param {{ current: { layer: { style: unknown } | null } }} ref */
function clearSearchBoundaryHighlight(ref) {
  const { layer } = ref.current;
  if (layer && 'style' in layer) {
    try {
      layer.style = null;
    } catch {
      /* noop */
    }
  }
  // eslint-disable-next-line no-param-reassign -- ref holder
  ref.current = { layer: null };
}

/**
 * Highlights the administrative boundary (Data-driven styling) when `mapId` is set and the
 * Cloud Map ID has the matching feature layer enabled.
 * @param {google.maps.Map} map
 * @param {unknown} place
 * @param {{ current: { layer: { style: unknown } | null } }} ref
 */
function applySearchBoundaryHighlight(map, place, ref) {
  clearSearchBoundaryHighlight(ref);
  const mapId = map.get('mapId');
  if (!mapId || typeof map.getFeatureLayer !== 'function') return;

  const rawId = place.id ?? place.place_id;
  const pid = normalizePlaceResourceId(rawId);
  if (!pid) return;

  const featureType = inferFeatureTypeFromPlaceTypes(place.types);
  if (featureType == null) return;

  try {
    const layer = map.getFeatureLayer(featureType);
    layer.style = options => {
      const fpid = normalizePlaceResourceId(options.feature?.placeId);
      if (fpid && fpid === pid) {
        return {
          strokeColor: '#1a73e8',
          strokeOpacity: 0.95,
          strokeWeight: 2,
          fillColor: '#1a73e8',
          fillOpacity: 0.12,
        };
      }
      return undefined;
    };
    // eslint-disable-next-line no-param-reassign -- ref holder
    ref.current = { layer };
  } catch {
    /* layer unavailable or not enabled for this Map ID */
  }
}

/**
 * Geocoder often returns tighter `bounds` than Places viewport for regions (similar to Maps search).
 * @param {unknown} place
 * @returns {Promise<google.maps.LatLngBounds|null>}
 */
function geocodePlaceToBounds(place) {
  return new Promise(resolve => {
    const g = window.google.maps;
    if (!g?.Geocoder) {
      resolve(null);
      return;
    }
    const geocoder = new g.Geocoder();
    const addr =
      (typeof place.formattedAddress === 'string' && place.formattedAddress) ||
      (typeof place.formatted_address === 'string' &&
        place.formatted_address) ||
      (typeof place.name === 'string' && place.name) ||
      (place.displayName &&
        typeof place.displayName === 'object' &&
        typeof place.displayName.text === 'string' &&
        place.displayName.text) ||
      '';

    let request = null;
    if (addr) {
      request = { address: String(addr) };
    } else if (place.location) {
      request = { location: place.location };
    }
    if (!request) {
      resolve(null);
      return;
    }

    geocoder.geocode(request, (results, status) => {
      if (status !== 'OK' || !results?.[0]?.geometry) {
        resolve(null);
        return;
      }
      const { bounds, viewport } = results[0].geometry;
      resolve(bounds || viewport || null);
    });
  });
}

const MapDrawingToolbar = forwardRef(
  (
    {
      map,
      zones,
      drawTargetZoneId,
      zoneVisibilitySelection,
      mapStructureRevision,
      purgeZoneIds,
      onPurgeComplete,
      onAreasChange,
      zonesFromParent,
      defaultCircleSpec,
      defaultZoneReady,
      controlledAreasFromParent,
      controlledAreasKey,
      /** When true, toolbar is laid out vertically and positioned by the parent (e.g. left middle). */
      embedded = false,
    },
    ref,
  ) => {
    const zonesRef = useRef(zones);
    zonesRef.current = zones;
    const drawTargetZoneIdRef = useRef(drawTargetZoneId);
    drawTargetZoneIdRef.current = drawTargetZoneId;

    const drawingManagerRef = useRef(null);
    const overlaysRef = useRef([]);
    /** @type {React.MutableRefObject<Array<google.maps.Polygon | google.maps.Circle | google.maps.Rectangle>>} */
    const orderedSelectionRef = useRef([]);
    /** @type {React.MutableRefObject<import('@turf/helpers').FeatureCollection[]>} */
    const historyRef = useRef([]);
    const historyIndexRef = useRef(0);
    const [, forceUpdate] = useReducer(n => n + 1, 0);

    const selectOverlay = useCallback((overlay, additive = false) => {
      const prev = orderedSelectionRef.current;
      const zr = zonesRef.current;
      let next;

      if (!additive) {
        prev.forEach(o => applyShapeStyleForOverlay(o, false, zr));
        next = [overlay];
        applyShapeStyleForOverlay(overlay, true, zr);
      } else {
        next = [...prev];
        const idx = next.indexOf(overlay);
        if (idx >= 0) {
          applyShapeStyleForOverlay(overlay, false, zr);
          next.splice(idx, 1);
        } else {
          next.push(overlay);
          applyShapeStyleForOverlay(overlay, true, zr);
        }
      }

      orderedSelectionRef.current = next;
      forceUpdate();
    }, []);

    const clearSelection = useCallback(() => {
      orderedSelectionRef.current.forEach(o =>
        applyShapeStyleForOverlay(o, false, zonesRef.current),
      );
      orderedSelectionRef.current = [];
      forceUpdate();
    }, []);

    const wireOverlayClick = useCallback(
      overlay => {
        overlay.addListener('click', ev => {
          if (typeof ev.stop === 'function') {
            ev.stop();
          }
          const dom = ev.domEvent;
          const additive = Boolean(
            dom?.ctrlKey || dom?.metaKey || dom?.shiftKey,
          );
          selectOverlay(overlay, additive);
        });
      },
      [selectOverlay],
    );

    const emitAreas = useCallback(() => {
      const list = overlaysRef.current
        .filter(o => getOverlayMeta(o)?.zoneId)
        .map(o => {
          const m = getOverlayMeta(o);
          const f = overlayToFeature(o);
          f.properties = {
            ...(f.properties || {}),
            ...(m?.zipCode ? { sxclZipCode: m.zipCode } : {}),
          };
          return {
            id: m.areaId,
            zoneId: m.zoneId,
            feature: f,
          };
        });
      onAreasChange?.(list);
    }, [onAreasChange]);

    const emitAreasRef = useRef(emitAreas);
    emitAreasRef.current = emitAreas;

    const geometryEditTimerRef = useRef(null);
    const schedulePolygonEditHistoryRef = useRef(() => {});
    const lastDefaultCircleKeyRef = useRef(null);
    const lastControlledAreasKeyRef = useRef(null);

    const pushHistory = useCallback(() => {
      const features = overlaysRef.current.map(o => {
        const f = overlayToFeature(o);
        const m = getOverlayMeta(o);
        f.properties = {
          ...(f.properties || {}),
          sxclAreaId: m?.areaId,
          sxclZoneId: m?.zoneId,
          ...(m?.zipCode ? { sxclZipCode: m.zipCode } : {}),
        };
        return f;
      });
      const snap = JSON.parse(JSON.stringify(featureCollection(features)));
      const idx = historyIndexRef.current;
      historyRef.current = historyRef.current.slice(0, idx + 1);
      historyRef.current.push(snap);
      historyIndexRef.current = historyRef.current.length - 1;

      while (historyRef.current.length > HISTORY_LIMIT) {
        historyRef.current.shift();
        historyIndexRef.current -= 1;
      }
      emitAreas();
      forceUpdate();
    }, [emitAreas]);

    const pushHistoryRef = useRef(pushHistory);
    pushHistoryRef.current = pushHistory;

    schedulePolygonEditHistoryRef.current = () => {
      emitAreasRef.current();
      if (geometryEditTimerRef.current) {
        clearTimeout(geometryEditTimerRef.current);
      }
      geometryEditTimerRef.current = setTimeout(() => {
        geometryEditTimerRef.current = null;
        pushHistoryRef.current();
      }, 450);
    };

    const restoreSnapshot = useCallback(
      snapshot => {
        if (!snapshot?.features) return;

        overlaysRef.current.forEach(o => {
          o.setMap(null);
        });
        overlaysRef.current = [];
        orderedSelectionRef.current.forEach(o => {
          try {
            applyShapeStyleForOverlay(o, false, zonesRef.current);
          } catch {
            /* overlay already removed */
          }
        });
        orderedSelectionRef.current = [];

        snapshot.features.forEach(f => {
          const props = f.properties || {};
          const zoneId = props.sxclZoneId;
          const polyColor =
            zonesRef.current.find(z => z.id === zoneId)?.color || '#1976d2';
          const polys = featureToGooglePolygons(map, f, {
            ...shapeStyleBaseFromZoneColor(polyColor, false),
            editable: true,
            draggable: true,
          });
          polys.forEach(p => {
            const baseMeta = {
              areaId: props.sxclAreaId || nanoid(),
              zoneId: props.sxclZoneId,
            };
            if (props.sxclZipCode) {
              baseMeta.zipCode = String(props.sxclZipCode);
            }
            setOverlayMeta(p, baseMeta);
            wireOverlayClick(p);
            attachPolygonGeometryListeners(p, () =>
              schedulePolygonEditHistoryRef.current(),
            );
            overlaysRef.current.push(p);
          });
        });
        emitAreas();
        forceUpdate();
      },
      [map, wireOverlayClick, emitAreas],
    );

    const handleUndo = useCallback(() => {
      if (historyIndexRef.current <= 0) return;
      historyIndexRef.current -= 1;
      restoreSnapshot(historyRef.current[historyIndexRef.current]);
    }, [restoreSnapshot]);

    const handleRedo = useCallback(() => {
      if (historyIndexRef.current >= historyRef.current.length - 1) return;
      historyIndexRef.current += 1;
      restoreSnapshot(historyRef.current[historyIndexRef.current]);
    }, [restoreSnapshot]);

    useEffect(() => {
      if (!map || !window.google?.maps?.drawing) return undefined;

      historyRef.current = [JSON.parse(JSON.stringify(featureCollection([])))];
      historyIndexRef.current = 0;

      const dm = new window.google.maps.drawing.DrawingManager({
        drawingControl: false,
        drawingMode: null,
        polygonOptions: shapeStyleNeutral(false),
        circleOptions: shapeStyleNeutral(false),
        rectangleOptions: shapeStyleNeutral(false),
      });
      dm.setMap(map);
      drawingManagerRef.current = dm;

      const drawingModeListener = window.google.maps.event.addListener(
        dm,
        'drawingmode_changed',
        () => {
          forceUpdate();
        },
      );

      const completeListener = window.google.maps.event.addListener(
        dm,
        'overlaycomplete',
        ({ overlay }) => {
          const g = window.google.maps;
          const target = drawTargetZoneIdRef.current;
          if (!target) {
            overlay.setMap(null);
            dm.setDrawingMode(null);
            return;
          }

          const meta = { areaId: nanoid(), zoneId: target };
          const zoneColor =
            zonesRef.current.find(z => z.id === target)?.color ?? '#1976d2';
          const styleOpts = shapeStyleBaseFromZoneColor(zoneColor, false);

          let shape = overlay;
          if (overlay instanceof g.Circle && g.geometry?.spherical) {
            shape = createEditablePolygonFromCircle(map, overlay, styleOpts);
            overlay.setMap(null);
            setOverlayMeta(shape, meta);
          } else if (overlay instanceof g.Rectangle) {
            shape = createEditablePolygonFromRectangle(map, overlay, styleOpts);
            overlay.setMap(null);
            setOverlayMeta(shape, meta);
          } else if (overlay instanceof g.Polygon) {
            setOverlayMeta(overlay, meta);
            enablePolygonVertices(overlay, styleOpts);
          } else {
            setOverlayMeta(overlay, meta);
          }

          overlaysRef.current = [...overlaysRef.current, shape];
          applyShapeStyleForOverlay(shape, false, zonesRef.current);
          wireOverlayClick(shape);
          if (shape instanceof g.Polygon) {
            attachPolygonGeometryListeners(shape, () =>
              schedulePolygonEditHistoryRef.current(),
            );
          }

          dm.setDrawingMode(null);
          pushHistory();
        },
      );

      const mapClickListener = map.addListener('click', () => {
        clearSelection();
      });

      return () => {
        if (geometryEditTimerRef.current) {
          clearTimeout(geometryEditTimerRef.current);
          geometryEditTimerRef.current = null;
        }
        window.google.maps.event.removeListener(drawingModeListener);
        window.google.maps.event.removeListener(completeListener);
        window.google.maps.event.removeListener(mapClickListener);
        overlaysRef.current.forEach(o => {
          o.setMap(null);
        });
        overlaysRef.current = [];
        orderedSelectionRef.current = [];
        dm.setMap(null);
        drawingManagerRef.current = null;
        lastDefaultCircleKeyRef.current = null;
        lastControlledAreasKeyRef.current = null;
      };
    }, [map, clearSelection, selectOverlay, wireOverlayClick, pushHistory]);

    useEffect(() => {
      if (!map) return undefined;
      const showAll = zoneVisibilitySelection.includes(ZONE_VISIBILITY_ALL);
      overlaysRef.current.forEach(o => {
        const zid = getOverlayMeta(o)?.zoneId;
        if (!zid) {
          o.setMap(map);
          return;
        }
        const visible = showAll || zoneVisibilitySelection.includes(zid);
        o.setMap(visible ? map : null);
      });
      return undefined;
    }, [map, zoneVisibilitySelection, mapStructureRevision]);

    useEffect(() => {
      if (!purgeZoneIds || purgeZoneIds.length === 0) return undefined;
      overlaysRef.current = overlaysRef.current.filter(o => {
        if (purgeZoneIds.includes(getOverlayMeta(o)?.zoneId)) {
          o.setMap(null);
          return false;
        }
        return true;
      });
      orderedSelectionRef.current = orderedSelectionRef.current.filter(
        o => !purgeZoneIds.includes(getOverlayMeta(o)?.zoneId),
      );
      pushHistoryRef.current();
      onPurgeComplete?.();
      return undefined;
    }, [purgeZoneIds, onPurgeComplete]);

    useEffect(() => {
      if (!Array.isArray(zonesFromParent)) {
        lastControlledAreasKeyRef.current = null;
      }
      if (zonesFromParent !== null) {
        lastDefaultCircleKeyRef.current = null;
      }
    }, [zonesFromParent]);

    useEffect(() => {
      if (!map) return undefined;
      if (zonesFromParent !== null) return undefined;
      if (!defaultCircleSpec || !defaultZoneReady) return undefined;
      if (!window.google.maps.geometry?.spherical) return undefined;

      const key = JSON.stringify(defaultCircleSpec);
      if (lastDefaultCircleKeyRef.current === key) return undefined;
      lastDefaultCircleKeyRef.current = key;

      overlaysRef.current = overlaysRef.current.filter(o => {
        const m = getOverlayMeta(o);
        if (m?.zoneId === DEFAULT_ZONE_ID) {
          o.setMap(null);
          return false;
        }
        return true;
      });

      const g = window.google.maps;
      const center = new g.LatLng(defaultCircleSpec.lat, defaultCircleSpec.lng);
      const circle = new g.Circle({
        center,
        radius: defaultCircleSpec.radiusMeters,
        map: null,
      });
      const styleOpts = shapeStyleBaseFromZoneColor(
        zonesRef.current.find(z => z.id === DEFAULT_ZONE_ID)?.color ??
          '#1976d2',
        false,
      );
      const poly = createEditablePolygonFromCircle(map, circle, styleOpts);
      setOverlayMeta(poly, {
        areaId: nanoid(),
        zoneId: DEFAULT_ZONE_ID,
      });
      overlaysRef.current = [...overlaysRef.current, poly];
      applyShapeStyleForOverlay(poly, false, zonesRef.current);
      wireOverlayClick(poly);
      attachPolygonGeometryListeners(poly, () =>
        schedulePolygonEditHistoryRef.current(),
      );
      pushHistoryRef.current();
      return undefined;
    }, [
      map,
      zonesFromParent,
      defaultCircleSpec,
      defaultZoneReady,
      wireOverlayClick,
    ]);

    useEffect(() => {
      if (!map) return undefined;
      if (!Array.isArray(zonesFromParent)) return undefined;
      if (
        controlledAreasKey === null ||
        controlledAreasKey === undefined ||
        controlledAreasFromParent === null ||
        controlledAreasFromParent === undefined
      ) {
        return undefined;
      }
      if (lastControlledAreasKeyRef.current === controlledAreasKey) {
        return undefined;
      }
      lastControlledAreasKeyRef.current = controlledAreasKey;

      overlaysRef.current.forEach(o => {
        o.setMap(null);
      });
      overlaysRef.current = [];
      orderedSelectionRef.current = [];

      controlledAreasFromParent.forEach(area => {
        const zoneColor =
          zonesRef.current.find(z => z.id === area.zoneId)?.color ?? '#1976d2';
        const polys = featureToGooglePolygons(map, area.feature, {
          ...shapeStyleBaseFromZoneColor(zoneColor, false),
          editable: true,
          draggable: true,
        });
        polys.forEach(p => {
          const zipFromFeature = area.feature?.properties?.sxclZipCode;
          setOverlayMeta(p, {
            areaId: area.id,
            zoneId: area.zoneId,
            ...(zipFromFeature ? { zipCode: String(zipFromFeature) } : {}),
          });
          wireOverlayClick(p);
          attachPolygonGeometryListeners(p, () =>
            schedulePolygonEditHistoryRef.current(),
          );
          overlaysRef.current.push(p);
        });
      });

      historyRef.current = [JSON.parse(JSON.stringify(featureCollection([])))];
      historyIndexRef.current = 0;
      pushHistoryRef.current();
      return undefined;
    }, [
      map,
      zonesFromParent,
      controlledAreasFromParent,
      controlledAreasKey,
      wireOverlayClick,
    ]);

    useEffect(() => {
      const dm = drawingManagerRef.current;
      if (!dm || typeof dm.setOptions !== 'function') return undefined;
      const z = zonesRef.current.find(
        x => x.id === drawTargetZoneIdRef.current,
      );
      const color = z?.color ?? '#1976d2';
      const opts = shapeStyleBaseFromZoneColor(color, false);
      const preview = { ...opts, editable: false, draggable: false };
      dm.setOptions({
        polygonOptions: preview,
        circleOptions: preview,
        rectangleOptions: preview,
      });
      return undefined;
    }, [drawTargetZoneId, zones]);

    useEffect(() => {
      if (!map) return undefined;
      const zr = zonesRef.current;
      const selected = orderedSelectionRef.current;
      overlaysRef.current.forEach(o => {
        applyShapeStyleForOverlay(o, selected.includes(o), zr);
      });
      return undefined;
    }, [map, zones]);

    const drawingMode = drawingManagerRef.current?.getDrawingMode?.() ?? null;

    const setMode = useCallback(mode => {
      const dm = drawingManagerRef.current;
      if (!dm) return;
      dm.setDrawingMode(mode);
      forceUpdate();
    }, []);

    const handleDeleteSelected = useCallback(() => {
      if (orderedSelectionRef.current.length === 0) return;
      orderedSelectionRef.current.forEach(o => {
        o.setMap(null);
        overlaysRef.current = overlaysRef.current.filter(x => x !== o);
      });
      orderedSelectionRef.current = [];
      pushHistory();
    }, [pushHistory]);

    const handleClearAll = useCallback(() => {
      if (overlaysRef.current.length === 0) return;
      overlaysRef.current.forEach(o => {
        o.setMap(null);
      });
      overlaysRef.current = [];
      orderedSelectionRef.current = [];
      pushHistory();
    }, [pushHistory]);

    const handleUnion = useCallback(() => {
      const selected = orderedSelectionRef.current;
      if (selected.length < 2) return;
      try {
        const mergedZoneId = resolveMergedZoneId(
          selected,
          drawTargetZoneIdRef.current,
        );
        if (!mergedZoneId) return;

        const fc = featureCollection(selected.map(o => overlayToFeature(o)));
        const merged = union(fc);
        if (!merged?.geometry) {
          return;
        }
        selected.forEach(o => {
          o.setMap(null);
          overlaysRef.current = overlaysRef.current.filter(x => x !== o);
        });
        orderedSelectionRef.current = [];

        const zoneColor =
          zonesRef.current.find(z => z.id === mergedZoneId)?.color ?? '#1976d2';
        const newPolys = featureToGooglePolygons(map, merged, {
          ...shapeStyleBaseFromZoneColor(zoneColor, false),
          editable: true,
          draggable: true,
        });
        newPolys.forEach(p => {
          setOverlayMeta(p, { areaId: nanoid(), zoneId: mergedZoneId });
          wireOverlayClick(p);
          attachPolygonGeometryListeners(p, () =>
            schedulePolygonEditHistoryRef.current(),
          );
          overlaysRef.current.push(p);
        });
        pushHistory();
      } catch {
        /* union failed (invalid geometry) */
      }
    }, [map, wireOverlayClick, pushHistory]);

    const handleSubtract = useCallback(() => {
      const selected = orderedSelectionRef.current;
      if (selected.length < 2) return;
      try {
        const mergedZoneId = resolveMergedZoneId(
          selected,
          drawTargetZoneIdRef.current,
        );
        if (!mergedZoneId) return;

        const fc = featureCollection(selected.map(o => overlayToFeature(o)));
        const result = difference(fc);

        selected.forEach(o => {
          o.setMap(null);
          overlaysRef.current = overlaysRef.current.filter(x => x !== o);
        });
        orderedSelectionRef.current = [];

        const zoneColor =
          zonesRef.current.find(z => z.id === mergedZoneId)?.color ?? '#1976d2';
        if (result?.geometry) {
          const newPolys = featureToGooglePolygons(map, result, {
            ...shapeStyleBaseFromZoneColor(zoneColor, false),
            editable: true,
            draggable: true,
          });
          newPolys.forEach(p => {
            setOverlayMeta(p, { areaId: nanoid(), zoneId: mergedZoneId });
            wireOverlayClick(p);
            attachPolygonGeometryListeners(p, () =>
              schedulePolygonEditHistoryRef.current(),
            );
            overlaysRef.current.push(p);
          });
        }
        pushHistory();
      } catch {
        /* difference failed (invalid geometry) */
      }
    }, [map, wireOverlayClick, pushHistory]);

    const handleIntersect = useCallback(() => {
      const selected = orderedSelectionRef.current;
      if (selected.length < 2) return;
      try {
        const mergedZoneId = resolveMergedZoneId(
          selected,
          drawTargetZoneIdRef.current,
        );
        if (!mergedZoneId) return;

        const fc = featureCollection(selected.map(o => overlayToFeature(o)));
        const result = intersect(fc);
        if (!result?.geometry) {
          return;
        }
        selected.forEach(o => {
          o.setMap(null);
          overlaysRef.current = overlaysRef.current.filter(x => x !== o);
        });
        orderedSelectionRef.current = [];

        const zoneColor =
          zonesRef.current.find(z => z.id === mergedZoneId)?.color ?? '#1976d2';
        const newPolys = featureToGooglePolygons(map, result, {
          ...shapeStyleBaseFromZoneColor(zoneColor, false),
          editable: true,
          draggable: true,
        });
        newPolys.forEach(p => {
          setOverlayMeta(p, { areaId: nanoid(), zoneId: mergedZoneId });
          wireOverlayClick(p);
          attachPolygonGeometryListeners(p, () =>
            schedulePolygonEditHistoryRef.current(),
          );
          overlaysRef.current.push(p);
        });
        pushHistory();
      } catch {
        /* intersect failed (invalid geometry) */
      }
    }, [map, wireOverlayClick, pushHistory]);

    const handleRemoveOverlap = useCallback(() => {
      const selected = orderedSelectionRef.current;
      if (selected.length < 2) return;
      try {
        const mergedZoneId = resolveMergedZoneId(
          selected,
          drawTargetZoneIdRef.current,
        );
        if (!mergedZoneId) return;

        const features = selected.map(o => overlayToFeature(o));
        const result = symmetricDifferenceMany(features);

        selected.forEach(o => {
          o.setMap(null);
          overlaysRef.current = overlaysRef.current.filter(x => x !== o);
        });
        orderedSelectionRef.current = [];

        const zoneColor =
          zonesRef.current.find(z => z.id === mergedZoneId)?.color ?? '#1976d2';
        if (result?.geometry) {
          const newPolys = featureToGooglePolygons(map, result, {
            ...shapeStyleBaseFromZoneColor(zoneColor, false),
            editable: true,
            draggable: true,
          });
          newPolys.forEach(p => {
            setOverlayMeta(p, { areaId: nanoid(), zoneId: mergedZoneId });
            wireOverlayClick(p);
            attachPolygonGeometryListeners(p, () =>
              schedulePolygonEditHistoryRef.current(),
            );
            overlaysRef.current.push(p);
          });
        }
        pushHistory();
      } catch {
        /* symmetric difference failed (invalid geometry) */
      }
    }, [map, wireOverlayClick, pushHistory]);

    const { OverlayType } = window.google.maps.drawing;
    const hasSelection = orderedSelectionRef.current.length > 0;
    const hasMultiSelection = orderedSelectionRef.current.length >= 2;
    const hasShapes = overlaysRef.current.length > 0;
    const canUndo = historyIndexRef.current > 0;
    const canRedo =
      historyIndexRef.current < historyRef.current.length - 1 &&
      historyRef.current.length > 0;
    const drawDisabled = !drawTargetZoneId;

    useImperativeHandle(ref, () => {
      const api = {
        /**
         * Adds editable polygon(s) from a GeoJSON Feature (Polygon or MultiPolygon).
         * @param {import('@turf/helpers').Feature} feature
         * @returns {boolean}
         */
        addPolygonsFromGeoJsonFeature(feature) {
          if (!map || !feature?.geometry || !drawTargetZoneIdRef.current) {
            return false;
          }
          const gt = feature.geometry.type;
          if (gt !== 'Polygon' && gt !== 'MultiPolygon') return false;

          const target = drawTargetZoneIdRef.current;
          const zoneColor =
            zonesRef.current.find(z => z.id === target)?.color ?? '#1976d2';
          const styleOpts = shapeStyleBaseFromZoneColor(zoneColor, false);
          const polys = featureToGooglePolygons(map, feature, {
            ...styleOpts,
            editable: true,
            draggable: true,
          });
          if (polys.length === 0) return false;

          polys.forEach(poly => {
            setOverlayMeta(poly, { areaId: nanoid(), zoneId: target });
            overlaysRef.current = [...overlaysRef.current, poly];
            applyShapeStyleForOverlay(poly, false, zonesRef.current);
            wireOverlayClick(poly);
            attachPolygonGeometryListeners(poly, () =>
              schedulePolygonEditHistoryRef.current(),
            );
          });
          clearSelection();
          selectOverlay(polys[polys.length - 1], false);
          pushHistory();
          return true;
        },

        /**
         * @param {google.maps.LatLngBounds} bounds
         * @returns {boolean}
         */
        addPolygonFromPlaceBounds(bounds) {
          const feature = polygonFeatureFromLatLngBounds(bounds);
          if (!feature) return false;
          return api.addPolygonsFromGeoJsonFeature(feature);
        },

        /**
         * Adds one editable polygon per postal code from geocoded bounds (GeoJSON rectangle).
         * @param {Array<{ zip: string; bounds: google.maps.LatLngBounds }>} entries
         * @returns {{ added: number; failedZips: string[] }}
         */
        bulkImportZipPolygons(entries) {
          if (!map || !drawTargetZoneIdRef.current || !Array.isArray(entries)) {
            return { added: 0, failedZips: [] };
          }
          const target = drawTargetZoneIdRef.current;
          const failedZips = [];
          let added = 0;
          const zoneColor =
            zonesRef.current.find(z => z.id === target)?.color ?? '#1976d2';
          const styleOpts = {
            ...shapeStyleBaseFromZoneColor(zoneColor, false),
            editable: true,
            draggable: true,
          };

          for (let i = 0; i < entries.length; i += 1) {
            const row = entries[i];
            const zip = row?.zip;
            const bounds = row?.bounds;
            if (!zip || !bounds) {
              if (zip) failedZips.push(String(zip));
            } else {
              const feature = polygonFeatureFromLatLngBounds(bounds);
              if (!feature) {
                failedZips.push(String(zip));
              } else {
                const polys = featureToGooglePolygons(map, feature, {
                  ...styleOpts,
                });
                if (polys.length === 0) {
                  failedZips.push(String(zip));
                } else {
                  polys.forEach(poly => {
                    const areaId = nanoid();
                    setOverlayMeta(poly, {
                      areaId,
                      zoneId: target,
                      zipCode: String(zip),
                    });
                    applyShapeStyleForOverlay(poly, false, zonesRef.current);
                    wireOverlayClick(poly);
                    attachPolygonGeometryListeners(poly, () =>
                      schedulePolygonEditHistoryRef.current(),
                    );
                    overlaysRef.current.push(poly);
                  });
                  added += 1;
                }
              }
            }
          }

          if (added === 0) {
            return { added: 0, failedZips };
          }
          clearSelection();
          selectOverlay(
            overlaysRef.current[overlaysRef.current.length - 1],
            false,
          );
          pushHistory();
          return { added, failedZips };
        },
      };
      return api;
    }, [map, pushHistory, wireOverlayClick, clearSelection, selectOverlay]);

    return (
      <Paper
        elevation={3}
        sx={{
          position: embedded ? 'relative' : 'absolute',
          left: embedded ? 'auto' : 12,
          top: embedded ? 'auto' : '50%',
          bottom: embedded ? 'auto' : 'auto',
          transform: embedded ? 'none' : 'translateY(-50%)',
          zIndex: 1,
          p: '5px',
          flexShrink: 0,
          '& .MuiIconButton-root': {
            width: 40,
            height: 40,
          },
        }}>
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          spacing={0.25}>
          <Tooltip
            title={<FormattedMessage id="PAN_MAP" defaultMessage="Pan map" />}>
            <span>
              <IconButton
                size="small"
                color={drawingMode == null ? 'primary' : 'default'}
                onClick={() => setMode(null)}
                aria-label="Pan map">
                <PanToolAltOutlined fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
          <Divider
            flexItem
            orientation="horizontal"
            sx={{ alignSelf: 'stretch', my: 0.25 }}
          />
          <Tooltip
            title={
              <FormattedMessage
                id="DRAW_POLYGON"
                defaultMessage="Draw polygon"
              />
            }>
            <span>
              <IconButton
                size="small"
                color={
                  drawingMode === OverlayType.POLYGON ? 'primary' : 'default'
                }
                disabled={drawDisabled}
                onClick={() => setMode(OverlayType.POLYGON)}
                aria-label="Draw polygon">
                <PolylineOutlined fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              <FormattedMessage id="DRAW_CIRCLE" defaultMessage="Draw circle" />
            }>
            <span>
              <IconButton
                size="small"
                color={
                  drawingMode === OverlayType.CIRCLE ? 'primary' : 'default'
                }
                disabled={drawDisabled}
                onClick={() => setMode(OverlayType.CIRCLE)}
                aria-label="Draw circle">
                <CircleOutlined fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              <FormattedMessage
                id="DRAW_RECTANGLE"
                defaultMessage="Draw rectangle"
              />
            }>
            <span>
              <IconButton
                size="small"
                color={
                  drawingMode === OverlayType.RECTANGLE ? 'primary' : 'default'
                }
                disabled={drawDisabled}
                onClick={() => setMode(OverlayType.RECTANGLE)}
                aria-label="Draw rectangle">
                <CropSquareOutlined fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
          <Divider
            flexItem
            orientation="horizontal"
            sx={{ alignSelf: 'stretch', my: 0.25 }}
          />
          <Tooltip
            title={
              <FormattedMessage
                id="MERGE_SELECTED_UNION_SELECT_SEVERAL_WITH_SHIFT_CTRL_CMDCLICK"
                defaultMessage="Merge selected (union). Select several with Shift/Ctrl/Cmd+click."
              />
            }>
            <span>
              <IconButton
                size="small"
                onClick={handleUnion}
                disabled={!hasMultiSelection}
                aria-label="Merge selected shapes">
                <MergeType fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              <FormattedMessage
                id="SUBTRACT_FROM_FIRST_REMOVES_OVERLAP_OF_ALL_OTHER_SELECTED_SHAPES_FROM_THE_FIRST_SHAPE_YOU_SELECTED"
                defaultMessage="Subtract from first: removes overlap of all other selected shapes from the first shape you selected."
              />
            }>
            <span>
              <IconButton
                size="small"
                onClick={handleSubtract}
                disabled={!hasMultiSelection}
                aria-label="Subtract overlapping shapes from the first selected">
                <ContentCut fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              <FormattedMessage
                id="KEEP_OVERLAP_ONLY_REGION_COMMON_TO_ALL_SELECTED_SHAPES_INTERSECTION"
                defaultMessage="Keep overlap only: region common to all selected shapes (intersection)."
              />
            }>
            <span>
              <IconButton
                size="small"
                onClick={handleIntersect}
                disabled={!hasMultiSelection}
                aria-label="Keep only overlapping region of selected shapes">
                <JoinInner fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              <FormattedMessage
                id="REMOVE_OVERLAP_DISCARD_SHARED_AREA_BETWEEN_SELECTIONS_KEEP_ONLY_PARTS_THAT_BELONG_TO_ONE_SHAPE_SYMMETRIC_DIFFERENCE"
                defaultMessage="Remove overlap: discard shared area between selections; keep only parts that belong to one shape (symmetric difference)."
              />
            }>
            <span>
              <IconButton
                size="small"
                onClick={handleRemoveOverlap}
                disabled={!hasMultiSelection}
                aria-label="Remove overlapping areas between selected shapes">
                <JoinFull fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
          <Divider
            flexItem
            orientation="horizontal"
            sx={{ alignSelf: 'stretch', my: 0.25 }}
          />
          <Tooltip title={<FormattedMessage id="UNDO" defaultMessage="Undo" />}>
            <span>
              <IconButton
                size="small"
                onClick={handleUndo}
                disabled={!canUndo}
                aria-label="Undo">
                <Undo fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={<FormattedMessage id="REDO" defaultMessage="Redo" />}>
            <span>
              <IconButton
                size="small"
                onClick={handleRedo}
                disabled={!canRedo}
                aria-label="Redo">
                <Redo fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
          <Divider
            flexItem
            orientation="horizontal"
            sx={{ alignSelf: 'stretch', my: 0.25 }}
          />
          <Tooltip
            title={
              <FormattedMessage
                id="DELETE_SELECTED_SHAPES"
                defaultMessage="Delete selected shapes"
              />
            }>
            <span>
              <IconButton
                size="small"
                onClick={handleDeleteSelected}
                disabled={!hasSelection}
                aria-label="Delete selected shapes">
                <DeleteOutline fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              <FormattedMessage
                id="CLEAR_ALL_SHAPES"
                defaultMessage="Clear all shapes"
              />
            }>
            <span>
              <IconButton
                size="small"
                onClick={handleClearAll}
                disabled={!hasShapes}
                aria-label="Clear all shapes">
                <DeleteSweep fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>
    );
  },
);

MapDrawingToolbar.displayName = 'MapDrawingToolbar';

/**
 * ZIP / postal code bulk import control (stacked under the drawing toolbar).
 * @param {{
 *   onZipBulkImportClick?: () => void;
 *   disabled?: boolean;
 *   disabledTitle?: string;
 * }} props
 */
function AddressImportToolbar({
  onZipBulkImportClick,
  disabled = false,
  disabledTitle = 'Bulk import is unavailable.',
}) {
  return (
    <Paper
      elevation={3}
      sx={{
        p: '5px',
        flexShrink: 0,
        '& .MuiIconButton-root': {
          width: 40,
          height: 40,
        },
      }}>
      <Stack direction="column" alignItems="center" spacing={0.25}>
        <Tooltip title={disabled ? disabledTitle : 'Postal code bulk import'}>
          <span>
            <IconButton
              color="primary"
              size="small"
              onClick={() => onZipBulkImportClick?.()}
              disabled={disabled}
              aria-label="ZIP bulk import">
              <ZipBulkImportIcon fontSize="medium" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

function ServiceAreaGoogleMap({
  apiKey,
  center,
  zoom,
  height,
  mapContainerStyle,
  options,
  children,
  onLoad,
  onUnmount,
  drawingControls,
  /** When true (default), show ZIP bulk import with the drawing tools when the map is ready. */
  importToolbar = true,
  onImportToolbarZipBulkImport,
  /** Fired on Save changes with `[{ zone: { name, colorCode, type }, geoJson: Feature[] }]`. */
  onSaveChanges,
  /** Same payload as `onSaveChanges` when the user saves the draw session. */
  onChange,
  /** `null` + radius + centerPoint → bootstrap a default service area and circle. `Array` → controlled service area list. `undefined` → internal list only. */
  zones: zonesProp,
  /** When `zones` is an array, optional area rows: `{ id, zoneId, feature }` (Turf GeoJSON Feature per shape). */
  areas: areasProp,
  /** Statute miles; used only when `zones === null` with `centerPoint`. Preferred over `radiusMeters`. */
  radiusMiles,
  /** Metres; used only when `zones === null` with `centerPoint` if `radiusMiles` is not set. */
  radiusMeters,
  /** `{ lat, lng }` in WGS84; used only when `zones === null` with a radius prop. */
  centerPoint,
  /** When `zones` prop is an array, notify parent of service area list edits from the map UI. */
  onZonesChange: onZonesChangeProp,
  /** ISO 3166-1 alpha-2 country for bulk postal import geocoding (e.g. US, CA, GB). */
  bulkImportCountryCode = 'US',
  sx,
  ...boxProps
}) {
  const [zones, setZones] = useState(
    /** @type {Array<{ id: string; name: string; color: string; zoneType: string }>} */ [],
  );
  const [areas, setAreas] = useState(
    /** @type {Array<{ id: string; zoneId: string; feature: import('@turf/helpers').Feature }>} */ [],
  );
  const [zoneVisibilitySelection, setZoneVisibilitySelection] = useState([
    ZONE_VISIBILITY_ALL,
  ]);
  const [drawTargetZoneId, setDrawTargetZoneId] = useState(
    /** @type {string | null} */ null,
  );
  const [mapStructureRevision, setMapStructureRevision] = useState(0);
  const [purgeZoneIds, setPurgeZoneIds] = useState(
    /** @type {string[] | null} */ null,
  );

  const resolvedRadiusMeters = useMemo(() => {
    if (radiusMiles != null && Number.isFinite(Number(radiusMiles))) {
      return Number(radiusMiles) * METERS_PER_STATUTE_MILE;
    }
    if (radiusMeters != null && Number.isFinite(Number(radiusMeters))) {
      return Number(radiusMeters);
    }
    return null;
  }, [radiusMiles, radiusMeters]);

  const resolvedBulkImportCountryCode = useMemo(
    () => normalizeBulkImportCountryCode(bulkImportCountryCode),
    [bulkImportCountryCode],
  );

  const bulkImportToolbarDisabled = zones.length === 0 || !drawTargetZoneId;
  const bulkImportToolbarDisabledTitle =
    zones.length === 0
      ? 'Add a service area before postal bulk import.'
      : 'Choose a service area to edit before postal bulk import.';

  const latestModelRef = useRef({ zones: [], areas: [] });
  latestModelRef.current = { zones, areas };

  const areasForBaselineRef = useRef(areas);
  areasForBaselineRef.current = areas;

  const [savedModelSignature, setSavedModelSignature] = useState(() =>
    serializeZoneModel([], []),
  );

  const zoneModelSignature = useMemo(
    () => serializeZoneModel(zones, areas),
    [zones, areas],
  );

  const hasUnsavedZoneModelChanges = zoneModelSignature !== savedModelSignature;

  useEffect(() => {
    if (!Array.isArray(zonesProp)) return;
    if (areasProp !== undefined) {
      setSavedModelSignature(
        serializeZoneModel(
          zonesProp,
          Array.isArray(areasProp) ? areasProp : [],
        ),
      );
    } else {
      setSavedModelSignature(
        serializeZoneModel(zonesProp, areasForBaselineRef.current),
      );
    }
  }, [zonesProp, areasProp]);

  const bumpMapRevision = useCallback(() => {
    setMapStructureRevision(n => n + 1);
  }, []);

  const handleAreasChange = useCallback(
    nextAreas => {
      setAreas(nextAreas);
      bumpMapRevision();
    },
    [bumpMapRevision],
  );

  const handleZonesChange = useCallback(
    next => {
      if (Array.isArray(zonesProp)) {
        onZonesChangeProp?.(next);
        return;
      }
      setZones(next);
    },
    [zonesProp, onZonesChangeProp],
  );

  const defaultZoneInitKeyRef = useRef(null);
  useEffect(() => {
    if (zonesProp !== null) {
      defaultZoneInitKeyRef.current = null;
      return;
    }
    if (
      resolvedRadiusMeters == null ||
      !centerPoint ||
      typeof centerPoint.lat !== 'number' ||
      typeof centerPoint.lng !== 'number'
    ) {
      return;
    }
    const initKey = JSON.stringify({
      radiusMeters: resolvedRadiusMeters,
      centerPoint,
    });
    if (defaultZoneInitKeyRef.current === initKey) return;
    defaultZoneInitKeyRef.current = initKey;
    setZones([{ ...DEFAULT_ZONE_DEF }]);
    setDrawTargetZoneId(DEFAULT_ZONE_ID);
    setZoneVisibilitySelection([DEFAULT_ZONE_ID]);
  }, [zonesProp, resolvedRadiusMeters, centerPoint]);

  useEffect(() => {
    if (!Array.isArray(zonesProp)) return;
    setZones(zonesProp);
  }, [zonesProp]);

  useEffect(() => {
    if (!Array.isArray(zonesProp)) return;
    if (areasProp === undefined) return;
    setAreas(Array.isArray(areasProp) ? areasProp : []);
    bumpMapRevision();
  }, [zonesProp, areasProp, bumpMapRevision]);

  const defaultCircleSpec = useMemo(() => {
    if (zonesProp !== null) return null;
    if (
      resolvedRadiusMeters == null ||
      !centerPoint ||
      typeof centerPoint.lat !== 'number' ||
      typeof centerPoint.lng !== 'number'
    ) {
      return null;
    }
    return {
      lat: centerPoint.lat,
      lng: centerPoint.lng,
      radiusMeters: resolvedRadiusMeters,
    };
  }, [zonesProp, resolvedRadiusMeters, centerPoint]);

  const defaultZoneReady = useMemo(
    () => zones.some(z => z.id === DEFAULT_ZONE_ID),
    [zones],
  );

  const controlledAreasKey = useMemo(() => {
    if (!Array.isArray(zonesProp)) return null;
    try {
      return JSON.stringify(areasProp ?? []);
    } catch {
      return String((areasProp ?? []).length);
    }
  }, [zonesProp, areasProp]);

  const mapCenter = useMemo(() => {
    if (
      zonesProp === null &&
      resolvedRadiusMeters != null &&
      centerPoint &&
      typeof centerPoint.lat === 'number' &&
      typeof centerPoint.lng === 'number'
    ) {
      return { lat: centerPoint.lat, lng: centerPoint.lng };
    }
    return center;
  }, [zonesProp, resolvedRadiusMeters, centerPoint, center]);

  const handleDeleteZonesRequest = useCallback(
    ids => {
      const nextZones = prev => prev.filter(z => !ids.includes(z.id));
      if (Array.isArray(zonesProp)) {
        const next = nextZones(zones);
        setDrawTargetZoneId(dt =>
          dt && ids.includes(dt) ? (next[0]?.id ?? null) : dt,
        );
        onZonesChangeProp?.(next);
      } else {
        setZones(prev => {
          const next = nextZones(prev);
          setDrawTargetZoneId(dt =>
            dt && ids.includes(dt) ? (next[0]?.id ?? null) : dt,
          );
          return next;
        });
      }
      setAreas(prev => prev.filter(a => !ids.includes(a.zoneId)));
      setZoneVisibilitySelection(prev => {
        const filtered = prev.filter(
          id => id === ZONE_VISIBILITY_ALL || !ids.includes(id),
        );
        if (
          filtered.length === 0 ||
          (filtered.length === 1 && filtered[0] === ZONE_VISIBILITY_ALL)
        ) {
          return [ZONE_VISIBILITY_ALL];
        }
        return filtered;
      });
      setPurgeZoneIds(ids);
    },
    [zonesProp, zones, onZonesChangeProp],
  );

  const handlePurgeComplete = useCallback(() => {
    setPurgeZoneIds(null);
  }, []);

  useEffect(() => {
    const alive = new Set(zones.map(z => z.id));
    setZoneVisibilitySelection(prev => {
      if (prev.includes(ZONE_VISIBILITY_ALL) && prev.length > 1) {
        return [ZONE_VISIBILITY_ALL];
      }
      const filtered = prev.filter(
        id => id === ZONE_VISIBILITY_ALL || alive.has(id),
      );
      if (filtered.length === 0) return [ZONE_VISIBILITY_ALL];
      return filtered;
    });
  }, [zones]);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    id: 'service-area-google-map',
    version: '3.64',
    libraries: ['drawing', 'geometry', 'places'],
  });

  const [map, setMap] = useState(null);
  const mapShellRef = useRef(/** @type {HTMLDivElement | null} */ null);
  const [mapShellFullscreen, setMapShellFullscreen] = useState(false);
  const getMapPortalContainer = useCallback(
    () => mapShellRef.current ?? document.body,
    [],
  );
  const mapDrawingToolbarRef = useRef(
    /** @type {{ addPolygonFromPlaceBounds?: (b: google.maps.LatLngBounds) => boolean; addPolygonsFromGeoJsonFeature?: (f: import('@turf/helpers').Feature) => boolean; bulkImportZipPolygons?: (e: Array<{ zip: string; bounds: google.maps.LatLngBounds }>) => { added: number; failedZips: string[] } } | null} */ null,
  );
  const searchBoundaryHighlightRef = useRef(
    /** @type {{ layer: { style: unknown } | null }} */ { layer: null },
  );
  const drawTargetZoneIdForSearchRef = useRef(drawTargetZoneId);
  drawTargetZoneIdForSearchRef.current = drawTargetZoneId;
  const drawingControlsForSearchRef = useRef(drawingControls);
  drawingControlsForSearchRef.current = drawingControls;

  const mergedMapContainerStyle = useMemo(
    () => ({
      width: '100%',
      height: '100%',
      ...mapContainerStyle,
    }),
    [mapContainerStyle],
  );

  const mergedOptions = useMemo(() => {
    const mapIdFromEnv = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID?.trim();
    return {
      ...defaultMapOptions,
      ...(mapIdFromEnv ? { mapId: mapIdFromEnv } : {}),
      ...options,
      /* Always off: fullscreen is handled on the outer shell so service area / draw UI stays visible. */
      fullscreenControl: false,
    };
  }, [options]);

  const handleMapLoad = useCallback(
    mapInstance => {
      setMap(mapInstance);
      onLoad?.(mapInstance);
    },
    [onLoad],
  );

  const handleMapUnmount = useCallback(() => {
    clearSearchBoundaryHighlight(searchBoundaryHighlightRef);
    setMap(null);
    setZones([]);
    setAreas([]);
    setZoneVisibilitySelection([ZONE_VISIBILITY_ALL]);
    setDrawTargetZoneId(null);
    setPurgeZoneIds(null);
    onUnmount?.();
  }, [onUnmount]);

  useEffect(() => {
    const syncShellFullscreen = () => {
      setMapShellFullscreen(getFullscreenElement() === mapShellRef.current);
    };
    document.addEventListener('fullscreenchange', syncShellFullscreen);
    document.addEventListener('webkitfullscreenchange', syncShellFullscreen);
    return () => {
      document.removeEventListener('fullscreenchange', syncShellFullscreen);
      document.removeEventListener(
        'webkitfullscreenchange',
        syncShellFullscreen,
      );
    };
  }, []);

  const canShellFullscreen = useMemo(() => {
    if (typeof document === 'undefined') return false;
    const el = document.createElement('div');
    return Boolean(
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.mozRequestFullScreen ||
      el.msRequestFullscreen,
    );
  }, []);

  const handleMapShellFullscreenToggle = useCallback(() => {
    const el = mapShellRef.current;
    if (!el) return;
    if (getFullscreenElement() === el) {
      const p = exitFullscreenDocument();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          /* exit fullscreen rejected or unsupported */
        });
      }
    } else {
      const p = requestFullscreenElement(el);
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          /* enter fullscreen rejected or unsupported */
        });
      }
    }
  }, []);

  const handleSaveDrawSession = useCallback(() => {
    const { zones: z, areas: a } = latestModelRef.current;
    const payload = buildZoneSavePayload(z, a);
    onChange?.(payload);
    onSaveChanges?.(payload);
    setSavedModelSignature(serializeZoneModel(z, a));
  }, [onChange, onSaveChanges]);

  const [zipBulkDialogOpen, setZipBulkDialogOpen] = useState(false);
  const [zipBulkText, setZipBulkText] = useState('');
  const [zipBulkBusy, setZipBulkBusy] = useState(false);
  const [zipBulkError, setZipBulkError] = useState('');
  const [zipBulkNotPlottedOpen, setZipBulkNotPlottedOpen] = useState(false);
  const [zipBulkNotPlottedZips, setZipBulkNotPlottedZips] = useState(
    /** @type {string[]} */ [],
  );

  const handleOpenZipBulkDialog = useCallback(() => {
    onImportToolbarZipBulkImport?.();
    setZipBulkError('');
    setZipBulkDialogOpen(true);
  }, [onImportToolbarZipBulkImport]);

  const handleCloseZipBulkDialog = useCallback(() => {
    if (zipBulkBusy) return;
    setZipBulkDialogOpen(false);
    setZipBulkError('');
  }, [zipBulkBusy]);

  const handleCloseZipBulkNotPlottedDialog = useCallback(() => {
    setZipBulkNotPlottedOpen(false);
    setZipBulkNotPlottedZips([]);
  }, []);

  const handleZipBulkSubmit = useCallback(async () => {
    if (!drawTargetZoneId) {
      setZipBulkError(
        'Choose a service area to edit (Edit new Service Area) before importing.',
      );
      return;
    }
    const zips = parseZipCodesFromCsv(zipBulkText);
    if (zips.length === 0) {
      setZipBulkError('Enter at least one postal code.');
      return;
    }
    if (zips.length > ZIP_IMPORT_LIMIT) {
      setZipBulkError(
        `Enter at most ${ZIP_IMPORT_LIMIT} postal codes per import.`,
      );
      return;
    }
    const api = mapDrawingToolbarRef.current;
    if (!map || !api?.bulkImportZipPolygons) {
      setZipBulkError('Map is not ready. Try again in a moment.');
      return;
    }

    setZipBulkBusy(true);
    setZipBulkError('');
    try {
      const { entries, geocodeFailed } = await geocodeZipListSequential(
        zips,
        resolvedBulkImportCountryCode,
      );

      const { added, failedZips: polyFailed } =
        api.bulkImportZipPolygons(entries);

      const polyFailedSet = new Set(polyFailed.map(z => String(z)));
      const notPlottedSorted = [
        ...new Set([...geocodeFailed, ...polyFailed].map(z => String(z))),
      ].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

      const plottedEntries = entries.filter(
        row => row?.zip && !polyFailedSet.has(String(row.zip)),
      );
      if (map && plottedEntries.length > 0) {
        const g = window.google.maps;
        const u = new g.LatLngBounds();
        plottedEntries.forEach(({ bounds: b }) => {
          if (b) u.union(b);
        });
        if (!u.isEmpty()) {
          map.fitBounds(u, {
            top: 100,
            right: 130,
            bottom: 200,
            left: 130,
          });
        }
      }

      if (added > 0) {
        setZipBulkDialogOpen(false);
        setZipBulkText('');
        setZipBulkError('');
        if (notPlottedSorted.length > 0) {
          setZipBulkNotPlottedZips(notPlottedSorted);
          setZipBulkNotPlottedOpen(true);
        }
        return;
      }
      const miss = notPlottedSorted.slice(0, 25);
      setZipBulkError(
        miss.length > 0
          ? `No areas added. Not plotted: ${miss.join(', ')}${notPlottedSorted.length > 25 ? '…' : ''}`
          : 'No areas added. Check postal codes and try again.',
      );
    } catch {
      setZipBulkError('Import failed unexpectedly. Try again.');
    } finally {
      setZipBulkBusy(false);
    }
  }, [drawTargetZoneId, zipBulkText, map, resolvedBulkImportCountryCode]);

  useEffect(() => {
    if (
      !map ||
      !drawingControls ||
      !window.google?.maps?.drawing ||
      !window.google?.maps?.places
    ) {
      return undefined;
    }

    const g = window.google.maps;
    const { places } = g;
    const pos = g.ControlPosition.TOP_CENTER;

    const container = document.createElement('div');
    container.style.padding = '8px';
    container.style.marginTop = '8px';
    container.style.backgroundColor = '#fff';
    container.style.borderRadius = '2px';
    container.style.boxShadow = '0 1px 4px rgba(0,0,0,0.3)';

    const mapFitPadding = {
      top: 100,
      right: 130,
      bottom: 200,
      left: 130,
    };

    const applyPlaceSearchResultToMap = (
      place,
      legacyGeometry,
      searchBoundsOverride,
    ) => {
      const feature = geoJsonFeatureFromGooglePlaceSearch(
        place,
        legacyGeometry,
        searchBoundsOverride,
      );
      if (!feature) return;
      const added =
        mapDrawingToolbarRef.current?.addPolygonsFromGeoJsonFeature?.(
          feature,
        ) ?? false;
      if (!added) return;
      const fit = latLngBoundsFromFeature(feature, g);
      if (fit) map.fitBounds(fit, mapFitPadding);
    };

    /** Prefer PlaceAutocompleteElement: legacy Autocomplete is not offered to new GCP projects (2025+). */
    if (typeof places.PlaceAutocompleteElement === 'function') {
      const el = new places.PlaceAutocompleteElement({
        locationBias: map.getBounds() ?? undefined,
      });
      el.setAttribute('aria-label', 'Search for places and addresses');
      el.setAttribute('placeholder', 'Search for places and addresses');
      Object.assign(el.style, {
        width: 'min(360px, 70vw)',
        fontSize: '15px',
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: '300',
        boxSizing: 'border-box',
      });
      container.appendChild(el);

      const syncBias = () => {
        const b = map.getBounds();
        if (b) el.locationBias = b;
      };
      const biasListener = g.event.addListener(map, 'bounds_changed', syncBias);
      syncBias();

      const onGmpSelect = async event => {
        if (!drawingControlsForSearchRef.current) return;
        const pred = event.placePrediction;
        if (!pred) return;

        if (!drawTargetZoneIdForSearchRef.current) {
          el.setAttribute(
            'title',
            'Choose a service area to edit (Edit new Service Area) before adding a search result.',
          );
          return;
        }

        try {
          const place = pred.toPlace();
          await place.fetchFields({
            fields: [
              'id',
              'types',
              'viewport',
              'location',
              'formattedAddress',
              'displayName',
            ],
          });
          applySearchBoundaryHighlight(map, place, searchBoundaryHighlightRef);
          const gcBounds = await geocodePlaceToBounds(place);
          applyPlaceSearchResultToMap(place, null, gcBounds);
          if (typeof el.value === 'string') el.value = '';
        } catch {
          /* fetchFields / place resolution failed */
        }
      };

      el.addEventListener('gmp-select', onGmpSelect);
      map.controls[pos].push(container);

      return () => {
        clearSearchBoundaryHighlight(searchBoundaryHighlightRef);
        g.event.removeListener(biasListener);
        el.removeEventListener('gmp-select', onGmpSelect);
        const arr = map.controls[pos].getArray();
        const idx = arr.indexOf(container);
        if (idx >= 0) map.controls[pos].removeAt(idx);
      };
    }

    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Search for places and addresses';
    input.setAttribute('aria-label', 'Search for places and addresses');
    Object.assign(input.style, {
      width: 'min(360px, 70vw)',
      fontSize: '15px',
      fontFamily: 'Roboto, Arial, sans-serif',
      fontWeight: '300',
      padding: '9px 12px',
      border: '1px solid #c6c6c6',
      borderRadius: '2px',
      boxSizing: 'border-box',
    });
    container.appendChild(input);

    let ac;
    try {
      ac = new places.Autocomplete(input, {
        fields: ['geometry', 'name', 'formatted_address', 'place_id', 'types'],
      });
    } catch {
      return undefined;
    }
    ac.bindTo('bounds', map);

    const listener = ac.addListener('place_changed', () => {
      if (!drawingControlsForSearchRef.current) return;

      const place = ac.getPlace();
      const geom = place.geometry;
      if (
        !extractExplicitGeoJsonFeature(place) &&
        !geom?.viewport &&
        !geom?.location
      ) {
        return;
      }

      if (!drawTargetZoneIdForSearchRef.current) {
        input.setAttribute(
          'title',
          'Choose a service area to edit (Edit new Service Area) before adding a search result.',
        );
        return;
      }

      applySearchBoundaryHighlight(map, place, searchBoundaryHighlightRef);
      geocodePlaceToBounds(place).then(gcBounds => {
        applyPlaceSearchResultToMap(place, geom, gcBounds);
      });
      input.value = '';
    });

    map.controls[pos].push(container);

    return () => {
      clearSearchBoundaryHighlight(searchBoundaryHighlightRef);
      try {
        g.event.removeListener(listener);
      } catch {
        /* noop */
      }
      g.event.clearInstanceListeners(ac);
      const arr = map.controls[pos].getArray();
      const idx = arr.indexOf(container);
      if (idx >= 0) map.controls[pos].removeAt(idx);
    };
  }, [map, drawingControls]);

  if (loadError) {
    return (
      <Box sx={{ p: 2, ...sx }} {...boxProps}>
        <Typography color="error">
          <FormattedMessage
            id="UNABLE_TO_LOAD_GOOGLE_MAPS_CHECK_THE_BROWSER_CONSOLE_AND_YOUR_API_KEY_CONFIGURATION"
            defaultMessage="Unable to load Google Maps. Check the browser console and your API key configuration."
          />
        </Typography>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box
        sx={{
          width: '100%',
          height,
          display: 'flex',
          alignItems: 'center',
          ...sx,
        }}
        {...boxProps}>
        <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
          <FormattedMessage id="LOADING_MAP" defaultMessage="Loading map" />
          ...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={mapShellRef}
      sx={{
        width: '100%',
        height,
        position: 'relative',
        '&:fullscreen': {
          width: '100vw',
          height: '100vh',
          boxSizing: 'border-box',
        },
        ...sx,
      }}
      {...boxProps}>
      {map && canShellFullscreen ? (
        <Tooltip
          title={
            mapShellFullscreen
              ? 'Exit fullscreen'
              : 'Fullscreen (map and tools)'
          }>
          <IconButton
            size="small"
            onClick={handleMapShellFullscreenToggle}
            aria-label={
              mapShellFullscreen
                ? 'Exit map fullscreen'
                : 'Enter map fullscreen'
            }
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 6,
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': { bgcolor: 'background.paper' },
            }}>
            {mapShellFullscreen ? (
              <FullscreenExit fontSize="small" />
            ) : (
              <Fullscreen fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      ) : null}
      <GoogleMap
        mapContainerStyle={mergedMapContainerStyle}
        center={mapCenter}
        zoom={zoom}
        options={mergedOptions}
        onLoad={handleMapLoad}
        onUnmount={handleMapUnmount}>
        {children}
      </GoogleMap>
      {drawingControls && map && window.google?.maps?.drawing && (
        <ZoneControls
          zones={zones}
          onZonesChange={handleZonesChange}
          zoneVisibilitySelection={zoneVisibilitySelection}
          onZoneVisibilitySelectionChange={setZoneVisibilitySelection}
          drawTargetZoneId={drawTargetZoneId}
          onDrawTargetZoneIdChange={setDrawTargetZoneId}
          onDeleteZonesRequest={handleDeleteZonesRequest}
          onSaveDrawSession={handleSaveDrawSession}
          hasUnsavedZoneModelChanges={hasUnsavedZoneModelChanges}
          getPortalContainer={getMapPortalContainer}
          drawingToolbar={
            <MapDrawingToolbar
              ref={mapDrawingToolbarRef}
              embedded
              map={map}
              zones={zones}
              drawTargetZoneId={drawTargetZoneId}
              zoneVisibilitySelection={zoneVisibilitySelection}
              mapStructureRevision={mapStructureRevision}
              purgeZoneIds={purgeZoneIds}
              onPurgeComplete={handlePurgeComplete}
              onAreasChange={handleAreasChange}
              zonesFromParent={zonesProp}
              defaultCircleSpec={defaultCircleSpec}
              defaultZoneReady={defaultZoneReady}
              controlledAreasFromParent={
                Array.isArray(zonesProp) ? areas : undefined
              }
              controlledAreasKey={controlledAreasKey}
            />
          }
          zipBulkImportToolbar={
            importToolbar ? (
              <AddressImportToolbar
                onZipBulkImportClick={handleOpenZipBulkDialog}
                disabled={bulkImportToolbarDisabled}
                disabledTitle={bulkImportToolbarDisabledTitle}
              />
            ) : null
          }
        />
      )}
      <Dialog
        open={zipBulkDialogOpen}
        onClose={handleCloseZipBulkDialog}
        maxWidth="sm"
        fullWidth
        container={getMapPortalContainer}
        aria-labelledby="zip-bulk-import-title">
        <DialogTitle id="zip-bulk-import-title">
          <FormattedMessage
            id="ZIPCODE_IMPORT"
            defaultMessage="Zipcode Import"
          />
        </DialogTitle>
        <DialogContent>
          {zipBulkError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {zipBulkError}
            </Alert>
          ) : null}
          <TextField
            autoFocus
            margin="dense"
            label="Zipcodes (CSV)"
            placeholder="90210, 10001, 60614"
            fullWidth
            multiline
            minRows={6}
            value={zipBulkText}
            onChange={e => setZipBulkText(e.target.value)}
            disabled={zipBulkBusy}
            helperText={
              <>
                <FormattedMessage
                  id="SERVICEAREA_LOOKUP_INSTRUCTION"
                  defaultMessage="Enter codes separated by commas, semicolons, or line breaks (CSV-style). Country for lookup:"
                />{' '}
                <strong>{resolvedBulkImportCountryCode}</strong>
                {resolvedBulkImportCountryCode === 'US' ? (
                  <FormattedMessage
                    id="EXAMPLES_90210_10001_60614_OR_94102_80202"
                    defaultMessage=". Examples: 90210, 10001, 60614 or 94102; 80202"
                  />
                ) : (
                  <FormattedMessage
                    id="USE_POSTAL_CODES_VALID_FOR_THAT_COUNTRY"
                    defaultMessage=". Use postal codes valid for that country."
                  />
                )}
              </>
            }
            FormHelperTextProps={{ component: 'div' }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseZipBulkDialog} disabled={zipBulkBusy}>
            <FormattedMessage id="CANCEL" defaultMessage="Cancel" />
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handleZipBulkSubmit().catch(() => {
                setZipBulkBusy(false);
                setZipBulkError('Import failed unexpectedly. Try again.');
              });
            }}
            disabled={zipBulkBusy || !zipBulkText.trim()}
            startIcon={
              zipBulkBusy ? (
                <CircularProgress color="inherit" size={18} />
              ) : null
            }>
            <FormattedMessage id="SUBMIT" defaultMessage="Submit" />
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={zipBulkNotPlottedOpen}
        onClose={handleCloseZipBulkNotPlottedDialog}
        maxWidth="sm"
        fullWidth
        container={getMapPortalContainer}
        aria-labelledby="zip-bulk-not-plotted-title">
        <DialogTitle id="zip-bulk-not-plotted-title">
          <FormattedMessage
            id="POSTAL_CODES_NOT_ADDED"
            defaultMessage="Postal codes not added"
          />
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <FormattedMessage
              id="MAP_VALID_CODES_WERE_IMPORTED_INVALID_EXCLUDED"
              defaultMessage="The following codes had no usable location data or could not be drawn on the map. Valid codes were imported."
            />
          </Typography>
          <List
            dense
            sx={{
              maxHeight: 320,
              overflow: 'auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}>
            {zipBulkNotPlottedZips.map(zip => (
              <ListItem key={zip} disablePadding sx={{ px: 2 }}>
                <ListItemText primary={zip} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={handleCloseZipBulkNotPlottedDialog}>
            <FormattedMessage id="OK" defaultMessage="Ok" />
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/** Embeds a Google Map (Maps JavaScript API). Requires REACT_APP_GOOGLE_MAPS_API_KEY. */
function ServiceAreaMap({
  center = defaultCenter,
  zoom = 6,
  height = 420,
  mapContainerStyle,
  options,
  children,
  onLoad,
  onUnmount,
  drawingControls = true,
  importToolbar = true,
  onImportToolbarZipBulkImport,
  onSaveChanges,
  onChange,
  zones,
  areas,
  radiusMiles,
  radiusMeters,
  centerPoint,
  onZonesChange,
  /** ISO 3166-1 alpha-2 country for bulk postal import (default US). */
  bulkImportCountryCode = 'US',
  sx,
  ...boxProps
}) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim();

  if (!apiKey) {
    return (
      <Box sx={{ p: 2, ...sx }} {...boxProps}>
        <Typography variant="body2" color="text.secondary">
          <FormattedMessage
            id="SET_REACTAPPGOOGLEMAPSAPIKEY_IN_YOUR_ENVIRONMENT_TO_SHOW_THE_MAP"
            defaultMessage="Set REACT_APP_GOOGLE_MAPS_API_KEY in your environment to show the map."
          />
        </Typography>
      </Box>
    );
  }

  return (
    <ServiceAreaGoogleMap
      apiKey={apiKey}
      center={center}
      zoom={zoom}
      height={height}
      mapContainerStyle={mapContainerStyle}
      options={options}
      onLoad={onLoad}
      onUnmount={onUnmount}
      drawingControls={drawingControls}
      importToolbar={importToolbar}
      onImportToolbarZipBulkImport={onImportToolbarZipBulkImport}
      onSaveChanges={onSaveChanges}
      onChange={onChange}
      zones={zones}
      areas={areas}
      radiusMiles={radiusMiles}
      radiusMeters={radiusMeters}
      centerPoint={centerPoint}
      onZonesChange={onZonesChange}
      bulkImportCountryCode={bulkImportCountryCode}
      sx={sx}
      {...boxProps}>
      {children}
    </ServiceAreaGoogleMap>
  );
}

export default ServiceAreaMap;
